/**
 * Building blocks shared by the long-form English guide pages
 * (what-is-digital-marketing, domain-authority guides, seo-expert-bangladesh,
 * best-digital-marketing-expert-bangladesh, seo-for/$industry). Route files keep
 * their own copy, schema and section order; these only render repeated markup.
 */
import type { ReactNode } from "react";
import { Link, type LinkProps } from "@tanstack/react-router";
import { ArrowUpRight, Check } from "lucide-react";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { FaqAccordion } from "@/components/FaqAccordion";
import { AiAnswersCallout } from "@/components/AiAnswersCallout";
import type { FaqItem } from "@/components/FaqAccordion";

type Card = { t: string; d: string };

/**
 * Two-column card grid. `reveal` fades cards in (alternating delay) and
 * stretches them to equal height; `check` adds a leading tick icon; `extra`
 * renders additional lines under the description.
 */
export function GuideCardGrid<T extends Card>({
  items,
  reveal = false,
  check = false,
  extra,
}: {
  items: readonly T[];
  reveal?: boolean;
  check?: boolean;
  extra?: (item: T) => ReactNode;
}) {
  return (
    <div className="grid md:grid-cols-2 gap-6 -mt-4">
      {items.map((item, i) => {
        const text = (
          <>
            <h3 className="font-display text-lg">{item.t}</h3>
            <p className="mt-2 text-sm text-muted-foreground">{item.d}</p>
            {extra?.(item)}
          </>
        );
        const body = check ? (
          <div className="flex items-start gap-3">
            <Check size={18} className="text-primary mt-1 flex-none" />
            <div>{text}</div>
          </div>
        ) : (
          text
        );
        return reveal ? (
          <Reveal key={item.t} delay={(i % 2) * 0.06}>
            <div className="rounded-3xl border border-border bg-card p-7 h-full">{body}</div>
          </Reveal>
        ) : (
          <div key={item.t} className="rounded-3xl border border-border bg-card p-7">
            {body}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Numbered step list. `anchors` gives each step an id (`step-1`, …) for HowTo
 * schema deep links; a step's optional `how` renders as an "In practice" note.
 */
export function GuideSteps({
  steps,
  anchors = false,
}: {
  steps: readonly (Card & { how?: string })[];
  anchors?: boolean;
}) {
  return (
    <ol className="space-y-5 -mt-4 max-w-3xl">
      {steps.map((s, i) => (
        <li
          key={s.t}
          id={anchors ? `step-${i + 1}` : undefined}
          className={`flex gap-5 rounded-3xl border border-border bg-card p-7${anchors ? " scroll-mt-32" : ""}`}
        >
          <span className="font-display text-2xl text-primary/70 leading-none">{i + 1}</span>
          <div>
            <h3 className="font-display text-lg">{s.t}</h3>
            <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
            {s.how && (
              <p className="mt-3 text-sm text-muted-foreground/80"><strong className="text-foreground/80">In practice:</strong> {s.how}</p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

export type GuideLink = { label: string } & Pick<LinkProps, "to" | "params">;

/**
 * Closing sections every guide ends with: related links + AI answers callout
 * (optionally introduced by `nextIntro`), the "People also ask" FAQ, and the
 * scoping-call / free-audit CTA.
 */
export function GuideFooter({
  nextEyebrow = "Next",
  nextTitle,
  nextIntro,
  links,
  aiContext,
  aiQuestions,
  faqs,
  faqIdPrefix,
  ctaTitle,
}: {
  nextEyebrow?: string;
  nextTitle: string;
  nextIntro?: ReactNode;
  links: readonly GuideLink[];
  aiContext: string;
  aiQuestions: string[];
  faqs: FaqItem[];
  faqIdPrefix: string;
  ctaTitle: ReactNode;
}) {
  return (
    <>
      <Section eyebrow={nextEyebrow} title={nextTitle}>
        {nextIntro}
        <div className="flex flex-wrap gap-3 -mt-2 text-sm">
          {links.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              params={l.params}
              className="rounded-full border border-border px-5 py-3 hover:bg-secondary"
            >
              {l.label}
            </Link>
          ))}
        </div>
        <AiAnswersCallout context={aiContext} questionSlugs={aiQuestions} />
      </Section>

      <Section eyebrow="FAQ" title="People also ask">
        <FaqAccordion items={faqs} idPrefix={faqIdPrefix} />
      </Section>

      <Section title={ctaTitle}>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium"
          >
            Book a scoping call <ArrowUpRight size={16} />
          </Link>
          <Link
            to="/free-seo-audit"
            className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-4 text-sm font-medium hover:bg-secondary"
          >
            Get a free audit <ArrowUpRight size={16} />
          </Link>
        </div>
      </Section>
    </>
  );
}
